/**
 * Training Distribution analytics — stacked bar + donut + weekly trend.
 *
 * Used both as a stand-alone page (`/staff/reports/training-distribution`)
 * and as an embedded card on the Athlete Detail page via the same component
 * tree (see TrainingDistributionCard.js for the compact athlete-detail variant).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GymBreakdown } from '../components/gym/GymBreakdown';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from 'recharts';
import {
  ArrowLeft, BarChart3, Activity, Filter, Download, Loader2, Heart, Shield,
} from 'lucide-react';
import { useSport } from '../context/SportContext';

// Color palette stable across all charts.
const CATEGORY_COLORS = {
  Match: '#ef4444',
  'Post-Match': '#f472b6',
  Training: '#10b981',
  Gym: '#f59e0b',
  Recovery: '#06b6d4',
  Prevention: '#6366f1',
  Tactical: '#3b82f6',
  Technical: '#a855f7',
  Situational: '#f97316',
  Aerobic: '#84cc16',
  // RTP must stand out — uses our amber/rose accent.
  RTP: '#fb923c',
  VBT: '#c084fc',
  Other: '#71717a',
};

const fallback = (label) => CATEGORY_COLORS[label] || '#71717a';

export default function TrainingDistributionPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { getSessionTypeMeta } = useSport();
  const [scope, setScope] = useState('team');
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [filters, setFilters] = useState({
    team_id: '',
    athlete_id: '',
    season: '',
    date_from: '',
    date_to: '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get('/teams').then(r => setTeams(r.data || [])).catch(() => {});
    api.get('/athletes').then(r => setAthletes(r.data || [])).catch(() => {});
  }, [api]);

  const fetchData = useCallback(async () => {
    if (scope === 'athlete' && !filters.athlete_id) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v != null) params[k] = v;
      });
      const path = scope === 'team' ? '/training-distribution/team' : '/training-distribution/athlete';
      const r = await api.get(path, { params });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load analytics');
    }
    setLoading(false);
  }, [api, scope, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const payload = {
        scope,
        language,
        organization_name: user?.organization_name || 'FB Performance',
      };
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v != null) payload[k] = k === 'season' ? Number(v) : v;
      });
      const res = await api.post('/training-distribution/export-pdf', payload, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `training_distribution_${scope}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to export PDF');
    }
    setExporting(false);
  };

  const donutData = useMemo(() => {
    if (!data) return [];
    return data.breakdown.filter(b => b.minutes > 0).map(b => ({
      name: b.category, value: b.minutes, percentage: b.percentage,
    }));
  }, [data]);

  const stackedBarData = useMemo(() => {
    if (!data) return [];
    return [data.breakdown.reduce((acc, b) => {
      acc[b.category] = b.minutes;
      return acc;
    }, { name: 'Total' })];
  }, [data]);

  const categories = data?.categories || [];

  return (
    <div className="min-h-screen bg-zinc-950 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <Button
          variant="ghost" size="sm" onClick={() => navigate('/staff/reports')}
          className="text-zinc-400 hover:text-zinc-100"
          data-testid="td-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Reports
        </Button>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-zinc-100 flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-amber-400" />
              {t('Training Distribution', 'Training Distribution')}
            </h1>
            <p className="text-zinc-500 text-sm mt-1">
              {t('Minutes & sessions across categories.', 'Minuti e sessioni per categoria.')} <span className="text-amber-400">{t('RTP tracked as standalone workload.', 'RTP tracciato come carico autonomo.')}</span>
            </p>
          </div>
          <Button
            onClick={handleExport}
            disabled={exporting || !data || data.total_minutes === 0}
            className="bg-amber-500 hover:bg-amber-400 text-zinc-950"
            data-testid="td-export-pdf"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            {t('Export PDF', 'Esporta PDF')}
          </Button>
        </div>

        {/* Scope tabs */}
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList className="bg-zinc-900/50 border border-zinc-800">
            <TabsTrigger value="team" data-testid="td-tab-team" className="data-[state=active]:bg-zinc-800">
              <Activity className="w-3.5 h-3.5 mr-1.5" /> {t('Team', 'Squadra')}
            </TabsTrigger>
            <TabsTrigger value="athlete" data-testid="td-tab-athlete" className="data-[state=active]:bg-zinc-800">
              <Heart className="w-3.5 h-3.5 mr-1.5" /> {t('Athlete', 'Atleta')}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Filters */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="td-filters">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
              <Filter className="w-4 h-4" /> {t('Filters', 'Filtri')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {scope === 'team' ? (
                <div>
                  <Label className="text-xs text-zinc-500">{t('Team', 'Squadra')}</Label>
                  <Select value={filters.team_id || 'ALL'} onValueChange={v => setFilters(f => ({ ...f, team_id: v === 'ALL' ? '' : v }))}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-sm" data-testid="td-filter-team"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="ALL">{t('All teams', 'Tutte le squadre')}</SelectItem>
                      {teams.map(tm => <SelectItem key={tm.id} value={tm.id}>{tm.code || tm.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="col-span-2">
                  <Label className="text-xs text-zinc-500">{t('Athlete', 'Atleta')} *</Label>
                  <Select value={filters.athlete_id} onValueChange={v => setFilters(f => ({ ...f, athlete_id: v }))}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-sm" data-testid="td-filter-athlete"><SelectValue placeholder={t('Select athlete', 'Seleziona atleta')} /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 max-h-72">
                      {athletes.map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs text-zinc-500">{t('Season', 'Stagione')}</Label>
                <Input
                  type="number" placeholder={t('e.g. 2024', 'es. 2024')}
                  value={filters.season}
                  onChange={e => setFilters(f => ({ ...f, season: e.target.value }))}
                  className="bg-zinc-950 border-zinc-800 text-sm"
                  data-testid="td-filter-season"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('From', 'Da')}</Label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
                  className="bg-zinc-950 border-zinc-800 text-sm"
                  data-testid="td-filter-from"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('To', 'A')}</Label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
                  className="bg-zinc-950 border-zinc-800 text-sm"
                  data-testid="td-filter-to"
                />
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <Button variant="ghost" size="sm"
                onClick={() => setFilters({ team_id: '', athlete_id: filters.athlete_id, season: '', date_from: '', date_to: '' })}
                className="text-zinc-400 hover:text-zinc-200"
                data-testid="td-reset">{t('Reset filters', 'Reimposta filtri')}</Button>
            </div>
          </CardContent>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <KPI label={scope === 'team' ? t('Calendar minutes', 'Minuti calendario') : t('Total Minutes', 'Minuti Totali')} value={scope === 'team' && data?.total_minutes == null ? '—' : (data?.total_minutes ?? 0).toLocaleString()} accent="amber" testid="kpi-total-min" />
          <KPI label={scope === 'team' ? t('Unique team sessions', 'Sessioni uniche squadra') : t('Total Sessions', 'Sessioni Totali')} value={data?.total_sessions ?? 0} accent="blue" testid="kpi-total-sessions" />
          <KPI label={t('RTP Minutes', 'Minuti RTP')} value={scope === 'team' && data?.rtp_minutes == null ? '—' : (data?.rtp_minutes ?? 0).toLocaleString()} accent="rose" testid="kpi-rtp-min" icon={Shield} />
          <KPI label={t('RTP Sessions', 'Sessioni RTP')} value={data?.rtp_sessions ?? 0} accent="rose" testid="kpi-rtp-sessions" icon={Shield} />
          <KPI label={scope === 'team' ? t('RTP calendar share', 'Quota RTP calendario') : t('RTP % of Load', 'RTP % del Carico')} value={scope === 'team' && data?.rtp_percentage == null ? '—' : `${data?.rtp_percentage ?? 0}%`} accent="rose" testid="kpi-rtp-pct" icon={Shield} />
        </div>

        {/* Charts */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('Loading…', 'Caricamento…')}
          </div>
        ) : !data || data.total_sessions === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-16 text-center text-zinc-500" data-testid="td-empty-state">
              <Activity className="w-10 h-10 mx-auto mb-3 opacity-50" />
              {t('No sessions match the current filters.', 'Nessuna sessione corrisponde ai filtri attuali.')}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Stacked Bar: time by category */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader><CardTitle className="text-sm text-zinc-400">{t('Time by Category (minutes)', 'Tempo per Categoria (minuti)')}</CardTitle></CardHeader>
              <CardContent>
                <div className="h-80" data-testid="td-chart-stacked-bar" style={{ minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                    <BarChart data={stackedBarData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                      <XAxis dataKey="name" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                      <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', color: '#fff' }} />
                      <Legend wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }} />
                      {categories.map(c => (
                        <Bar key={c} dataKey={c} stackId="a" fill={fallback(c)} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Donut */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader><CardTitle className="text-sm text-zinc-400">Category Percentages</CardTitle></CardHeader>
              <CardContent>
                <div className="h-80" data-testid="td-chart-donut">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData}
                        dataKey="value" nameKey="name"
                        cx="50%" cy="50%" outerRadius={100} innerRadius={55}
                        label={(e) => `${e.name} (${e.percentage}%)`}
                        labelLine={false}
                      >
                        {donutData.map((e) => (
                          <Cell key={e.name} fill={fallback(e.name)} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', color: '#fff' }}
                        formatter={(v, n) => [`${v.toLocaleString()} min`, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Weekly trend */}
            {data.weekly && data.weekly.length > 0 && (
              <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
                <CardHeader><CardTitle className="text-sm text-zinc-400">{t('Weekly Trend (Total & RTP minutes)', 'Trend Settimanale (minuti Totali & RTP)')}</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-72" data-testid="td-chart-weekly">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.weekly}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                        <XAxis dataKey="week" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                        <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', color: '#fff' }} />
                        <Legend wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }} />
                        <Line type="monotone" dataKey="total" name={t('Total minutes', 'Minuti totali')} stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} />
                        <Line type="monotone" dataKey="RTP" name={t('RTP minutes', 'Minuti RTP')} stroke="#fb923c" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="lg:col-span-2 min-w-0"><GymBreakdown data={data.gym_breakdown} prefix="td-gym-breakdown" /></div>
            {/* Breakdown table */}
            <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
              <CardHeader><CardTitle className="text-sm text-zinc-400">{t('Category Breakdown', 'Dettaglio per Categoria')}</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto" data-testid="td-breakdown-table">
                  <table className="w-full text-sm">
                    <thead className="text-zinc-500 text-xs">
                      <tr className="border-b border-zinc-800">
                        <th className="text-left py-2 px-3">{t('Category', 'Categoria')}</th>
                        <th className="text-right py-2 px-3">{t('Sessions', 'Sessioni')}</th>
                        <th className="text-right py-2 px-3">{t('Minutes', 'Minuti')}</th>
                        <th className="text-right py-2 px-3">{t('Percentage', 'Percentuale')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.breakdown.map(row => {
                        const isRtp = row.category === 'RTP';
                        return (
                          <tr key={row.category} className={`border-b border-zinc-800/50 ${isRtp ? 'bg-amber-500/5' : ''}`} data-testid={`td-row-${row.category}`}>
                            <td className="py-2 px-3">
                              <span className="inline-flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: fallback(row.category) }} />
                                <span className={isRtp ? 'text-amber-300 font-semibold' : ''}>{row.category}</span>
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right text-zinc-200">{row.sessions}</td>
                            <td className="py-2 px-3 text-right text-zinc-200">{row.minutes == null ? '—' : row.minutes.toLocaleString()}</td>
                            <td className="py-2 px-3 text-right text-zinc-400">{row.percentage == null ? '—' : `${row.percentage}%`}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Phase 1.6.5 — Session Type Distribution */}
            {(data.session_type_distribution && data.session_type_distribution.length > 0) && (
              <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2" data-testid="td-session-type-card">
                <CardHeader>
                  <CardTitle className="text-sm text-zinc-400">
                    {t('Session Type Distribution', 'Distribuzione per Tipo di Sessione')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto" data-testid="td-session-type-table">
                    <table className="w-full text-sm">
                      <thead className="text-zinc-500 text-xs">
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-2 px-3">{t('Session Type', 'Tipo di Sessione')}</th>
                          <th className="text-right py-2 px-3">{t('Sessions', 'Sessioni')}</th>
                          <th className="text-right py-2 px-3">{t('Minutes', 'Minuti')}</th>
                          <th className="text-right py-2 px-3">{t('Percentage', 'Percentuale')}</th>
                        </tr>
                      </thead>
                      <tbody className="text-zinc-300">
                        {data.session_type_distribution.map((row, idx) => {
                          const meta = getSessionTypeMeta(row.type);
                          return (
                          <tr key={row.type} className="border-b border-zinc-800/50" data-testid={`td-session-type-row-${row.type}`}>
                            <td className="py-2 px-3">
                              <span className="inline-flex items-center gap-2">
                                <span className="text-zinc-500 text-xs">{idx + 1}.</span>
                                <span className="font-medium" data-testid={`td-session-type-label-${row.type}`}>{meta.label || row.type}</span>
                                <span className="text-[10px] text-zinc-600 font-mono">({row.type})</span>
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right text-zinc-200">{row.sessions}</td>
                            <td className="py-2 px-3 text-right text-zinc-200">{row.minutes == null ? '—' : row.minutes.toLocaleString()}</td>
                            <td className="py-2 px-3 text-right text-zinc-400">{row.percentage == null ? '—' : `${row.percentage}%`}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, accent, testid, icon: Icon }) {
  const colorMap = {
    amber: 'text-amber-400 bg-amber-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    rose: 'text-rose-400 bg-rose-500/10',
    emerald: 'text-emerald-400 bg-emerald-500/10',
  };
  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid={testid}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <p className="text-zinc-400 text-xs uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-zinc-100 mt-1">{value}</p>
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorMap[accent]}`}>
          {Icon ? <Icon className="w-4 h-4" /> : <BarChart3 className="w-4 h-4" />}
        </div>
      </CardContent>
    </Card>
  );
}
